import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { BulkDeleteBar, BulkSelectCheckbox, BulkSelectAllCheckbox } from '../../components/BulkDelete';
import {
  Users, Mail, Phone, Search, Download, RefreshCw, Filter, X,
  CalendarClock, Send, UserPlus, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

import { AddCustomerDialog } from '../../components/crm/AddCustomerDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

const STATUS_META = {
  new: { label: 'New', color: 'bg-[#FF3B6B]/10 text-[#FF3B6B]' },
  email_sent: { label: 'Email sent', color: 'bg-[#06D6A0]/10 text-[#058B69]' },
  called_interested: { label: 'Interested', color: 'bg-[#06D6A0]/10 text-[#058B69]' },
  called_not_interested: { label: 'Not interested', color: 'bg-[#6B6B7A]/15 text-[#6B6B7A]' },
  follow_up: { label: 'Follow-up', color: 'bg-[#FFD166]/30 text-[#9C6B12]' },
  converted: { label: 'Converted', color: 'bg-[#FF3B6B]/15 text-[#FF3B6B]' },
  archived: { label: 'Archived', color: 'bg-[#E8E5DF] text-[#1E1E24]' },
};

const statusBadge = (s) => {
  const m = STATUS_META[s] || { label: s, color: 'bg-[#E8E5DF] text-[#1E1E24]' };
  return <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${m.color}`}>{m.label}</span>;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const fmtPhone = (raw) => {
  const d = (raw || '').replace(/\D+/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return raw;
};

export const CrmProspects = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', consented: 'all', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [templates, setTemplates] = useState([]);
  const [followUpsToday, setFollowUpsToday] = useState({ count: 0, entries: [] });

  // Manage dialog
  const [selected, setSelected] = useState(null);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [logs, setLogs] = useState([]);
  const [action, setAction] = useState('email'); // 'email' | 'call'
  const [templateId, setTemplateId] = useState('');
  const [callResult, setCallResult] = useState('interested');
  const [followUpDate, setFollowUpDate] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [manualStatus, setManualStatus] = useState('');
  const [savingAction, setSavingAction] = useState(false);

  // Convert to customer
  const [convertProspect, setConvertProspect] = useState(null);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/marketing-templates`, { withCredentials: true });
      setTemplates(res.data.templates || []);
      if ((res.data.templates || []).length) setTemplateId(res.data.templates[0].template_id);
    } catch (e) { /* non-fatal */ }
  };

  const fetchFollowUps = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/contact-inquiries/follow-ups-today`, { withCredentials: true });
      setFollowUpsToday(res.data || { count: 0, entries: [] });
    } catch (e) { /* non-fatal */ }
  };

  const fetchList = async (resetPage = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.set('status', filters.status);
      if (filters.consented === 'yes') params.set('consented', 'true');
      if (filters.search) params.set('search', filters.search);
      const skip = resetPage ? 0 : page * PAGE_SIZE;
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));
      const res = await axios.get(`${API_URL}/api/admin/contact-inquiries?${params.toString()}`, { withCredentials: true });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      if (resetPage) setPage(0);
    } catch (e) {
      toast.error('Failed to load prospects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); fetchFollowUps(); }, []);
  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => { fetchList(true); /* eslint-disable-next-line */ }, [filters.status, filters.consented, filters.search]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);
  const hasActiveFilters = filters.status !== 'all' || filters.consented !== 'all' || filters.search;

  const openManage = async (p) => {
    setSelected(p);
    setAction('email');
    setActionNotes('');
    setCallResult('interested');
    setFollowUpDate(p.follow_up_date || '');
    setManualStatus(p.status);
    try {
      const res = await axios.get(`${API_URL}/api/admin/contact-inquiries/${p.inquiry_id}`, { withCredentials: true });
      setLogs(res.data.logs || []);
    } catch (e) { setLogs([]); }
  };

  const saveManualStatus = async () => {
    if (!selected) return;
    if (manualStatus === selected.status && !followUpDate) return;
    try {
      const payload = { status: manualStatus };
      if (followUpDate) payload.follow_up_date = followUpDate;
      await axios.put(`${API_URL}/api/admin/contact-inquiries/${selected.inquiry_id}`, payload, { withCredentials: true });
    } catch (e) { /* handled in saveAction if called from there */ }
  };

  const saveAction = async () => {
    if (!selected) return;
    setSavingAction(true);
    try {
      if (action === 'email') {
        if (!templateId) { toast.error('Pick a template first'); setSavingAction(false); return; }
        await axios.post(`${API_URL}/api/admin/contact-inquiries/${selected.inquiry_id}/email`,
          { template_id: templateId, notes: actionNotes }, { withCredentials: true });
        toast.success('Marketing email sent');
      } else {
        const payload = { result: callResult, notes: actionNotes };
        if (callResult === 'call_back_later') {
          if (!followUpDate) { toast.error('Pick a follow-up date'); setSavingAction(false); return; }
          payload.follow_up_date = followUpDate;
        }
        await axios.post(`${API_URL}/api/admin/contact-inquiries/${selected.inquiry_id}/call-log`,
          payload, { withCredentials: true });
        toast.success('Call logged');
      }
      // Apply any manual status override
      if (manualStatus && manualStatus !== 'auto') {
        await saveManualStatus();
      }
      setSelected(null);
      fetchList();
      fetchFollowUps();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setSavingAction(false);
    }
  };

  const exportCsv = (consentedOnly = false) => {
    const qs = consentedOnly ? '?consented=true' : '';
    window.location.href = `${API_URL}/api/admin/contact-inquiries/export.csv${qs}`;
  };

  const resetFilters = () => {
    setFilters({ status: 'all', consented: 'all', search: '' });
    setSearchInput('');
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-6 h-6 text-[#FF3B6B]" />
            <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Prospect Clients
            </h1>
          </div>
          <p className="text-[#6B6B7A] text-sm">
            Leads captured from the Contact Us form. {total > 0 && <span className="font-medium">{total} prospects</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => fetchList()} className="rounded-full border-[#E8E5DF]" data-testid="prospects-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" onClick={() => exportCsv(false)} className="rounded-full border-[#E8E5DF]" data-testid="prospects-export-all">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={() => exportCsv(true)} className="rounded-full bg-[#06D6A0] hover:bg-[#05B88A] text-white" data-testid="prospects-export-consented">
            <Download className="w-4 h-4 mr-2" /> Consented only
          </Button>
        </div>
      </div>

      {/* Follow-ups today widget */}
      {followUpsToday.count > 0 && (
        <div className="bg-[#FFF4E6] border border-[#FFD166] rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="prospects-follow-ups-widget">
          <CalendarClock className="w-5 h-5 text-[#9C6B12] mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-[#1E1E24] text-sm">
              {followUpsToday.count} prospect{followUpsToday.count > 1 ? 's' : ''} scheduled for follow-up today
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {followUpsToday.entries.slice(0, 5).map((p) => (
                <button
                  key={p.inquiry_id}
                  onClick={() => openManage(p)}
                  className="text-xs px-2 py-1 rounded-full bg-white border border-[#E8E5DF] hover:border-[#FF3B6B] transition-colors"
                  data-testid={`follow-up-chip-${p.inquiry_id}`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 mb-6">
        <div className="flex items-center gap-2 mb-3 text-[#6B6B7A] text-sm">
          <Filter className="w-4 h-4" /> Filters
          {hasActiveFilters && (
            <button onClick={resetFilters} className="ml-auto text-xs flex items-center gap-1 text-[#FF3B6B] hover:underline" data-testid="prospects-clear-filters">
              <X className="w-3 h-3" /> Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger data-testid="prospects-filter-status" className="rounded-xl border-[#E8E5DF]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.consented} onValueChange={(v) => setFilters((f) => ({ ...f, consented: v }))}>
            <SelectTrigger data-testid="prospects-filter-consent" className="rounded-xl border-[#E8E5DF]">
              <SelectValue placeholder="Consent" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All prospects</SelectItem>
              <SelectItem value="yes">Marketing-consented</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative col-span-1 sm:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B7A]" />
            <Input
              placeholder="Search name, email, phone, message…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setFilters((f) => ({ ...f, search: searchInput.trim() })); }}
              className="pl-9 rounded-xl border-[#E8E5DF]"
              data-testid="prospects-filter-search"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <BulkSelectAllCheckbox ids={entries.map((p) => p.inquiry_id)} selected={bulkSelected} setSelected={setBulkSelected} testid="prospects-select-all" />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Inquiry</TableHead>
              <TableHead className="w-36">Received</TableHead>
              <TableHead className="w-32">Status</TableHead>
              <TableHead className="w-64 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-[#6B6B7A]">Loading…</TableCell></TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-[#6B6B7A]" data-testid="prospects-empty">
                  No prospects yet. Contact form submissions will appear here.
                </TableCell>
              </TableRow>
            ) : entries.map((p) => (
              <TableRow key={p.inquiry_id} className="hover:bg-[#FFFDF8]" data-testid={`prospect-row-${p.inquiry_id}`}>
                <TableCell>
                  <BulkSelectCheckbox id={p.inquiry_id} selected={bulkSelected} setSelected={setBulkSelected} testid={`prospect-checkbox-${p.inquiry_id}`} />
                </TableCell>
                <TableCell className="font-medium text-[#1E1E24]">
                  {p.name}
                  {p.consented && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-[#06D6A0]/10 text-[#058B69]">✓ Marketing OK</span>}
                </TableCell>
                <TableCell className="text-xs text-[#6B6B7A]">
                  <p className="flex items-center gap-1"><Mail className="w-3 h-3" /> {p.email}</p>
                  <p className="flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {fmtPhone(p.phone)}</p>
                </TableCell>
                <TableCell className="max-w-[280px] text-sm text-[#1E1E24] truncate" title={p.message}>
                  {p.message}
                </TableCell>
                <TableCell className="text-xs text-[#6B6B7A] whitespace-nowrap">{fmtDate(p.created_at)}</TableCell>
                <TableCell>
                  {p.converted_to_client ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#06D6A0]/10 text-[#058B69] font-medium" data-testid={`prospect-converted-badge-${p.inquiry_id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Customer
                    </span>
                  ) : (
                    statusBadge(p.status)
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 justify-end">
                    {!p.converted_to_client && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConvertProspect(p)}
                        className="rounded-full border-[#06D6A0] text-[#058B69] hover:bg-[#06D6A0]/10"
                        data-testid={`prospect-add-customer-${p.inquiry_id}`}
                        title="Add as customer (records this conversion to you)"
                      >
                        <UserPlus className="w-4 h-4 mr-1" /> Add as Customer
                      </Button>
                    )}
                    {p.converted_to_client ? (
                      <Button
                        size="sm"
                        onClick={() => {
                          const targetEmail = p.converted_to_customer_email || p.email;
                          navigate(`/crm/customers?email=${encodeURIComponent(targetEmail)}`);
                        }}
                        className="rounded-full bg-[#06D6A0] hover:bg-[#058B69] text-white"
                        data-testid={`prospect-view-customer-${p.inquiry_id}`}
                        title="Open this customer's profile"
                      >
                        View Customer
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => openManage(p)} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid={`prospect-manage-${p.inquiry_id}`}>
                        Manage
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-[#6B6B7A]">Page {page + 1} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="prospects-prev-page">Previous</Button>
            <Button variant="outline" size="sm" className="rounded-full" disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)} data-testid="prospects-next-page">Next</Button>
          </div>
        </div>
      )}

      {/* Manage dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="prospect-manage-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Manage prospect: {selected?.name}
            </DialogTitle>
            <DialogDescription>
              {selected?.email} · {fmtPhone(selected?.phone || '')}
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-4">
              <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3 text-sm">
                <p className="text-[#6B6B7A] text-xs mb-1">Inquiry ({fmtDate(selected.created_at)})</p>
                <p className="text-[#1E1E24]">"{selected.message}"</p>
              </div>

              <div>
                <Label className="text-sm font-medium text-[#1E1E24] mb-2 block">Action</Label>
                <RadioGroup value={action} onValueChange={setAction} className="space-y-2" data-testid="prospect-action-radio">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <RadioGroupItem value="email" id="action-email" data-testid="prospect-action-email" />
                    <div>
                      <span className="text-sm font-medium text-[#1E1E24]">Send marketing email / promotion</span>
                      <p className="text-xs text-[#6B6B7A]">Uses a pre-saved template and logs the send.</p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <RadioGroupItem value="call" id="action-call" data-testid="prospect-action-call" />
                    <div>
                      <span className="text-sm font-medium text-[#1E1E24]">Call client & log result</span>
                      <p className="text-xs text-[#6B6B7A]">Record the outcome and optionally set a follow-up.</p>
                    </div>
                  </label>
                </RadioGroup>
              </div>

              {action === 'email' && (
                <div data-testid="prospect-email-panel">
                  <Label className="text-sm font-medium text-[#1E1E24] mb-1 block">Template</Label>
                  <Select value={templateId} onValueChange={setTemplateId}>
                    <SelectTrigger className="rounded-xl border-[#E8E5DF]" data-testid="prospect-template-select">
                      <SelectValue placeholder="Pick a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {templateId && (
                    <p className="text-xs text-[#6B6B7A] mt-2">
                      Subject preview: <em>{(templates.find((t) => t.template_id === templateId)?.subject || '').replace('{name}', selected.name)}</em>
                    </p>
                  )}
                </div>
              )}

              {action === 'call' && (
                <div data-testid="prospect-call-panel" className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium text-[#1E1E24] mb-1 block">Call result</Label>
                    <Select value={callResult} onValueChange={setCallResult}>
                      <SelectTrigger className="rounded-xl border-[#E8E5DF]" data-testid="prospect-call-result">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="interested">Interested — send info</SelectItem>
                        <SelectItem value="not_interested">Not interested — archive</SelectItem>
                        <SelectItem value="call_back_later">Call back later</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {callResult === 'call_back_later' && (
                    <div>
                      <Label className="text-sm font-medium text-[#1E1E24] mb-1 block">Follow-up date</Label>
                      <Input
                        type="date"
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                        className="rounded-xl border-[#E8E5DF]"
                        data-testid="prospect-follow-up-date"
                      />
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label className="text-sm font-medium text-[#1E1E24] mb-1 block">Notes (optional)</Label>
                <Textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  rows={2}
                  placeholder="Any extra context about this interaction…"
                  className="rounded-xl border-[#E8E5DF]"
                  data-testid="prospect-action-notes"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-[#1E1E24] mb-1 block">Current status (manual override)</Label>
                <Select value={manualStatus} onValueChange={setManualStatus}>
                  <SelectTrigger className="rounded-xl border-[#E8E5DF]" data-testid="prospect-manual-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {logs.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[#6B6B7A] uppercase tracking-wide mb-2">Previous activity</p>
                  <div className="space-y-1 max-h-36 overflow-y-auto pr-1" data-testid="prospect-logs-list">
                    {logs.map((l) => (
                      <div key={l.log_id} className="text-xs p-2 rounded-lg bg-[#FFFDF8] border border-[#E8E5DF]">
                        <span className="font-medium text-[#1E1E24]">{l.action === 'email_sent' ? 'Email' : 'Call'}</span>
                        {l.result && <span className="text-[#6B6B7A]"> · {l.result}</span>}
                        <span className="text-[#6B6B7A]"> · {fmtDate(l.created_at)}</span>
                        {l.notes && <p className="text-[#6B6B7A] mt-0.5">{l.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} className="rounded-full" data-testid="prospect-cancel">Cancel</Button>
            <Button onClick={saveAction} disabled={savingAction} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="prospect-save">
              <Send className="w-4 h-4 mr-2" />
              {savingAction ? 'Saving…' : 'Save & Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkDeleteBar
        section="contact_inquiries"
        selectedIds={bulkSelected}
        onClearSelection={() => setBulkSelected([])}
        onDone={() => fetchList()}
        labelSingular="prospect"
        dataTestPrefix="prospects-bulk"
      />

      {/* Convert prospect → customer */}
      <AddCustomerDialog
        open={!!convertProspect}
        onOpenChange={(o) => { if (!o) setConvertProspect(null); }}
        inquiryId={convertProspect?.inquiry_id}
        initialData={convertProspect ? {
          name: convertProspect.name || '',
          email: convertProspect.email || '',
          phone: convertProspect.phone || '',
          notes: convertProspect.message || '',
        } : null}
        title="Add prospect as Customer"
        description={convertProspect
          ? `Convert "${convertProspect.name}" into a customer record. Fields are pre-filled from their inquiry — adjust anything that needs fixing.`
          : undefined}
        submitLabel="Convert & Add"
        onCreated={() => {
          setConvertProspect(null);
          fetchList();
        }}
      />
    </div>
  );
};

export default CrmProspects;
